'use client'
import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useLanguage } from '@/lib/context/LanguageContext'
import { creatorCategories } from '@/lib/data/network'
import { RegistryIcon, type IconKey } from '@/components/ui/iconRegistry'
import { provinceById, type ProvinceInfo } from '@/lib/data/provinceData'
import { toPersianNums } from '@/lib/persian'
import { SlideFromRight, FadeInUp } from '@/components/ui/FadeInUp'
import IranMap from '@/components/ui/IranMap'

// ── number formatting ──────────────────────────────────────────────────────────
const sep = (n: number, fa: boolean) => {
  const s = n.toLocaleString('en-US')
  return fa ? toPersianNums(s).replace(/,/g, '٬') : s
}
const dec = (n: number, fa: boolean) =>
  fa ? toPersianNums(String(n)).replace('.', '٫') : String(n)
const big = (n: number, fa: boolean): { v: string; u: string } => {
  if (n >= 1_000_000) return { v: dec(Math.round(n / 100_000) / 10, fa), u: fa ? 'میلیون' : 'M' }
  return { v: sep(Math.round(n / 1000), fa), u: fa ? 'هزار' : 'K' }
}

const iconKeyByEn: Record<string, IconKey> = Object.fromEntries(
  creatorCategories.map((c) => [c.en, c.iconKey]),
)

// ── shared stat building blocks ────────────────────────────────────────────────
function StatBox({ value, unit, label }: { value: string; unit?: string; label: string }) {
  return (
    <div
      className="rounded-lg p-2.5 flex flex-col justify-between"
      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
    >
      <div className="flex items-baseline gap-0.5 flex-wrap">
        <span
          className="font-display font-black text-[15px] leading-none"
          style={{
            background: 'linear-gradient(135deg,#60a5fa,#00b4d8)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {value}
        </span>
        {unit && (
          <span className="font-bold text-[10px] leading-none" style={{ color: '#00b4d8' }}>
            {unit}
          </span>
        )}
      </div>
      <span
        className="text-[9px] font-medium mt-1.5 block leading-tight"
        style={{ color: 'rgba(255,255,255,0.38)' }}
      >
        {label}
      </span>
    </div>
  )
}

function StatRow({ value, label }: { value: string; label: string }) {
  return (
    <div
      className="flex items-center justify-between px-3 py-2 rounded-lg"
      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}
    >
      <span className="text-[10px] font-medium" style={{ color: 'rgba(255,255,255,0.38)' }}>
        {label}
      </span>
      <span
        className="font-display font-black text-[15px] leading-none"
        style={{
          background: 'linear-gradient(135deg,#60a5fa,#00b4d8)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}
      >
        {value}
      </span>
    </div>
  )
}

// ── summary (no province hovered) ─────────────────────────────────────────────
function SummaryView({ l }: { l: 'en' | 'fa' }) {
  const fa = l === 'fa'
  return (
    <div className="flex flex-col justify-between h-full gap-3">
      <div>
        <h3
          className="font-display font-black text-[17px] leading-snug"
          style={{ color: 'rgba(255,255,255,0.92)' }}
        >
          {fa ? '۳۱ استان، یک کمپین یکپارچه.' : '31 Provinces, One Integrated Campaign.'}
        </h3>
        <p className="text-[10px] leading-relaxed mt-1" style={{ color: 'rgba(255,255,255,0.38)' }}>
          {fa
            ? 'نشانگر را روی هر استان ببرید تا داده های آن‌هارا ببینید'
            : 'Hover over each province to view its data.'}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <StatBox
          value={fa ? '+۱۰ هزار' : '+10,000'}
          label={fa ? 'اینفلوئنسر استانی' : 'Provincial Influencers'}
        />
        <StatBox
          value={fa ? '+۱۰۰' : '+100'}
          unit={fa ? 'میلیون' : 'M'}
          label={fa ? 'بازدید ماهانه' : 'Monthly Reach'}
        />
      </div>
      <StatRow
        value={fa ? '۷ هزار' : '+7,000'}
        label={fa ? 'کمپین اجرا شده' : 'Campaigns Executed'}
      />
    </div>
  )
}

// ── province detail view ───────────────────────────────────────────────────────
function ProvinceView({ p, l }: { p: ProvinceInfo; l: 'en' | 'fa' }) {
  const fa = l === 'fa'
  const reach = big(p.reach, fa)
  const pop = big(p.population, fa)
  return (
    <div className="flex flex-col justify-between h-full gap-2.5">
      <div>
        <p
          className="text-[9px] font-bold uppercase tracking-[0.2em] mb-0.5"
          style={{ color: '#00b4d8' }}
        >
          {fa ? 'استان' : 'Province'}
        </p>
        <h3
          className="font-display font-black text-[17px] leading-tight"
          style={{ color: 'rgba(255,255,255,0.92)' }}
        >
          {fa ? p.nameFA : p.nameEN}
        </h3>
        <p className="text-[10px] mt-0.5" style={{ color: 'rgba(255,255,255,0.38)' }}>
          {fa
            ? `مرکز: ${p.capitalFA} · جمعیت ${pop.v} ${pop.u}`
            : `Capital: ${p.capitalEN} · ${pop.v}${pop.u}`}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <StatBox
          value={sep(p.creators, fa) + '+'}
          label={fa ? 'اینفلوئنسر رده‌بندی شده' : 'Ranked influencers'}
        />
        <StatBox
          value={reach.v + '+'}
          unit={reach.u}
          label={fa ? 'بازدید ماهانه' : 'Monthly views'}
        />
      </div>
      <div className="flex flex-wrap gap-1.5">
        {p.categories.slice(0, 4).map((cat) => {
          const iconKey = iconKeyByEn[cat.en]
          return (
            <span
              key={cat.en}
              className="flex items-center gap-1 text-[9px] font-semibold px-2 py-1 rounded-full"
              style={{
                background: 'rgba(0,180,216,0.08)',
                color: '#00b4d8',
                border: '1px solid rgba(255,255,255,0.10)',
              }}
            >
              {iconKey && <RegistryIcon name={iconKey} className="w-2.5 h-2.5" />}
              {fa ? cat.fa : cat.en}
            </span>
          )
        })}
      </div>
      <StatRow
        value={fa ? sep(p.engagement, true) : sep(p.engagement, false)}
        label={fa ? 'کمپین‌های بومی‌سازی‌شده' : 'Localized campaigns'}
      />
    </div>
  )
}

// ── brand data ────────────────────────────────────────────────────────────────
const BRANDS = [
  {
    brandEN: 'Solico Kalleh Group',
    brandFA: 'سولیکو کاله',
    roleEN: 'Social Media Senior Expert',
    roleFA: 'کارشناس ارشد سوشال مدیا',
    quoteEN:
      'A highly professional influencer marketing experience. Their agility, precision in reporting, and effective communication with influencers made our campaign execution completely seamless and stress-free.',
    quoteFA:
      'تجربه‌ای بسیار حرفه‌ای در اینفلوئنسر مارکتینگ؛ سرعت عمل بالا در اجرا، دقت در گزارش‌دهی و تعامل بسیار مؤثر با اینفلوئنسرها، اجرای کمپین‌های ما را با کمترین دغدغه ممکن ساخت.',
    logo: '/clients/fmcg/kaleh.svg',
    logoBg: 'rgba(255,255,255,0.1)',
  },
  {
    brandEN: 'Padideh Shimi Gharn',
    brandFA: 'پدیده شیمی قرن',
    roleEN: 'Digital Marketing Manager',
    roleFA: 'مدیر دیجیتال مارکتینگ',
    quoteEN:
      'Trend Agency is a reliable and dedicated partner. Their unwavering commitment to our briefs, fair pricing, and proactive support during crises make them an ideal choice.',
    quoteFA:
      'آژانس ترندسازان خلاق، شریکی قابل اعتماد و دلسوز است. تعهد بی‌نظیر آن‌ها در اجرای دقیق بریف‌ها، هزینه‌های منصفانه و همراهی مسئولانه در شرایط بحرانی، آن‌ها را به انتخابی ایده‌آل تبدیل کرده است.',
    logo: '/clients/fmcg/padideh-shimi-gharn.svg',
    logoBg: 'rgba(255,255,255,0.1)',
  },
  {
    brandEN: 'PindoBaby',
    brandFA: 'پینوبیبی',
    roleEN: 'Marketing Expert',
    roleFA: 'کارشناس مارکتینگ',
    quoteEN:
      'Their greatest strength is their dedication; they treat our projects as if they were their own. Beyond just selling media, their genuine commitment and positive energy truly set them apart.',
    quoteFA:
      'نقطه قوت اصلی تیم شما، دلسوزی و نگاهی است که به پروژه‌ها دارند؛ انگار برای کسب‌وکار خودشان فعالیت می‌کنند. فراتر از فروش رسانه، تعهد و انرژی مثبت شما در این همکاری واقعاً متمایز است.',
    logo: '/clients/fmcg/pinoo-baby.svg',
    logoBg: 'rgba(255,255,255,0.1)',
  },
  {
    brandEN: 'Wallex',
    brandFA: 'والکس',
    roleEN: 'Social Media Manager',
    roleFA: 'سوشال مدیا منجر',
    quoteEN:
      'Six years of consistent, successful collaboration with Trend speaks for itself. High accountability, rapid execution without compromising quality, and fair pricing are the key reasons for our long-term trust.',
    quoteFA:
      'شش سال همکاری مداوم و موفق با «ترند» گویای همه چیز است. مسئولیت‌پذیری بالا، سرعت عمل در کنار حفظ کیفیت و قیمت‌گذاری منصفانه، دلایل اصلی تداوم این اعتماد و رضایت طی سالیان بوده است.',
    logo: '/clients/fintech/wallex.svg',
    logoBg: 'rgba(255,255,255,0.1)',
  },
  {
    brandEN: 'SnappTrip',
    brandFA: 'اسنپ تریپ',
    roleEN: 'Social Media Manager',
    roleFA: 'سوشال مدیا منجر',
    quoteEN:
      'Speed, high-quality execution, and consistent responsiveness are the standout qualities of Trend Agency. These attributes have turned our collaboration into a highly successful and seamless experience.',
    quoteFA:
      'سرعت عمل، کیفیت اجرایی بالا و پاسخگویی مداوم، سه ویژگی بارز آژانس ترند است که همکاری با آن‌ها را به تجربه‌ای موفق و بدون چالش برای ما تبدیل کرده است.',
    logo: '/clients/tech/snapptrip.svg',
    logoBg: 'rgba(255,255,255,0.1)',
  },
]

// ── brand quote carousel (auto-rotating) ──────────────────────────────────────
function BrandQuoteCarousel({ l }: { l: 'en' | 'fa' }) {
  const fa = l === 'fa'
  const [idx, setIdx] = useState(0)

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % BRANDS.length), 3800)
    return () => clearInterval(t)
  }, [])

  const b = BRANDS[idx]

  return (
    <div
      className="relative rounded-xl border border-white/8 overflow-hidden h-full"
      style={{ background: '#0b1629' }}
    >
      {/* radial glow top-end */}
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden
        style={{
          background:
            'radial-gradient(ellipse 90% 60% at 100% 0%, rgba(3,53,183,0.22) 0%, transparent 70%)',
        }}
      />
      {/* top gradient line */}
      <div
        className="absolute top-0 inset-x-0 h-[2px] pointer-events-none"
        aria-hidden
        style={{ background: 'linear-gradient(90deg, #0335b7, #00b4d8)' }}
      />

      <div className="relative h-full flex flex-col p-4 sm:p-5 gap-3">
        {/* eyebrow */}
        <p
          className="text-[10px] font-bold uppercase tracking-[0.2em] flex-shrink-0"
          style={{ color: '#00b4d8' }}
        >
          {fa ? 'نظر مشتریان' : 'Client Reviews'}
        </p>

        {/* animated quote content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={b.brandEN}
            className="flex flex-col gap-3 flex-1 min-h-0"
            initial={{ opacity: 0, x: fa ? -10 : 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: fa ? 10 : -10 }}
            transition={{ duration: 0.32, ease: 'easeOut' }}
          >
            {/* brand identity */}
            <div className="flex items-center gap-2.5">
              <span
                className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 flex items-center justify-center p-2"
                style={{ background: b.logoBg, border: '1px solid rgba(255,255,255,0.12)' }}
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={b.logo}
                  alt={b.brandEN}
                  width={48}
                  height={48}
                  className="w-full h-full object-contain"
                />
              </span>
              <span
                className="font-bold text-[13px] leading-tight"
                style={{ color: 'rgba(255,255,255,0.88)' }}
              >
                {fa ? b.brandFA : b.brandEN}
              </span>
            </div>

            {/* quote */}
            <div className="flex-1 flex flex-col gap-1 min-h-0">
              <span
                className="font-display font-black text-[32px] leading-none select-none"
                style={{ color: '#00b4d8', opacity: 0.35, lineHeight: 1 }}
              >
                &ldquo;
              </span>
              <p
                className="text-[11px] leading-relaxed"
                style={{ color: 'rgba(255,255,255,0.55)', marginTop: '-8px' }}
              >
                {fa ? b.quoteFA : b.quoteEN}
              </p>
            </div>

            {/* role attribution */}
            <p
              className="text-[10px] font-medium flex-shrink-0"
              style={{ color: 'rgba(255,255,255,0.32)' }}
            >
              &mdash; {fa ? b.roleFA : b.roleEN}
            </p>
          </motion.div>
        </AnimatePresence>

        {/* progress pill dots */}
        <div className="flex gap-1.5 justify-center flex-shrink-0">
          {BRANDS.map((brand, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              aria-label={fa ? `نمایش نظر ${brand.brandFA}` : `Show ${brand.brandEN} testimonial`}
              aria-current={i === idx ? 'true' : undefined}
              className="rounded-full outline-none cursor-pointer"
              style={{
                width: i === idx ? 16 : 5,
                height: 5,
                background: i === idx ? '#00b4d8' : 'rgba(255,255,255,0.18)',
                transition: 'width 0.3s ease, background 0.3s ease',
              }}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

// ── main component ─────────────────────────────────────────────────────────────
export default function ChannelsMessengers() {
  const { lang } = useLanguage()
  const l = lang as 'en' | 'fa'
  const [activeId, setActiveId] = useState<string | null>(null)
  const active = activeId ? provinceById[activeId] : null

  return (
    <div className="flex flex-col gap-3 lg:gap-4">
      {/* top row: Map (left) | Coverage stats (center) | Brand quotes (right) */}
      <div className="flex flex-col sm:flex-row gap-3 md:gap-4 items-start">
        {/* Map — LEFT, square */}
        <div
          className="relative rounded-xl overflow-hidden border border-white/8 w-full sm:w-[310px] sm:flex-shrink-0 sm:h-[310px]"
          style={{ background: '#0b1629' }}
        >
          <div
            className="absolute inset-0 pointer-events-none"
            aria-hidden
            style={{
              background:
                'radial-gradient(ellipse 70% 60% at 50% 50%, rgba(3,53,183,0.25) 0%, transparent 70%)',
            }}
          />
          <div className="relative sm:absolute sm:inset-0 flex flex-col p-2 sm:p-3">
            <div className="sm:flex-1 sm:min-h-0">
              <IranMap activeId={activeId} onHover={setActiveId} onSelect={setActiveId} />
            </div>
            <div className="flex items-center justify-center mt-1">
              <div
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full"
                style={{
                  background: 'rgba(255,255,255,0.07)',
                  border: '1px solid rgba(0,180,216,0.25)',
                }}
              >
                <span className="font-display font-black text-base text-white leading-none">
                  {l === 'fa' ? toPersianNums(31) : 31}
                </span>
                <span className="text-white/55 text-[10px] font-medium">
                  {l === 'fa' ? 'استان تحت پوشش' : 'Provinces covered'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Coverage stats — CENTER, flex-1 */}
        <SlideFromRight delay={0.08} className="w-full sm:flex-1 sm:min-w-0" margin="0px">
          <div
            className="relative rounded-xl border border-white/8 overflow-hidden h-auto min-h-[220px] sm:h-[310px] sm:min-h-0"
            style={{ background: '#0b1629' }}
          >
            <div
              className="absolute inset-0 pointer-events-none"
              aria-hidden
              style={{
                background:
                  'radial-gradient(ellipse 80% 60% at 20% 40%, rgba(3,53,183,0.22) 0%, transparent 70%)',
              }}
            />
            <div
              className="absolute top-0 inset-x-0 h-[2px] pointer-events-none"
              aria-hidden
              style={{ background: 'linear-gradient(90deg, #0335b7, #00b4d8)' }}
            />
            <div className="relative h-full p-3 sm:p-4 overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.div
                  key={active ? active.id : 'summary'}
                  className="h-full"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.25, ease: 'easeOut' }}
                >
                  {active ? <ProvinceView p={active} l={l} /> : <SummaryView l={l} />}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </SlideFromRight>

        {/* Brand quote carousel — RIGHT, fixed width */}
        <FadeInUp delay={0.14} className="w-full sm:flex-shrink-0 sm:w-[260px]" margin="0px">
          <div className="h-[240px] sm:h-[310px]">
            <BrandQuoteCarousel l={l} />
          </div>
        </FadeInUp>
      </div>
    </div>
  )
}
